import { useEffect, useState } from 'react'
import { apiQueryClient } from 'features/api/apiQueryClient'
import {
  GRAPH_SAMPLE_INTERVAL_MS,
  LIVE_TICK_INTERVAL_MS,
  MAX_SAMPLES
} from './performanceMonitorBar.constants'
import {
  QueryCacheEntry,
  QueryCacheSample,
  QueryCacheStats
} from './performanceMonitorBar.types'

type QueryCacheMeasurement = {
  entries: QueryCacheEntry[]
  stats: QueryCacheStats
}

const measureQueryCache = (): QueryCacheMeasurement => {
  const rawEntries = apiQueryClient.getQueryCache().getAll()
  const entries = rawEntries.flatMap((entry): QueryCacheEntry[] => {
    if (entry.state.data === undefined) return []
    try {
      const bytes = new Blob([JSON.stringify(entry.state.data)]).size
      return [
        {
          key: JSON.stringify(entry.queryKey),
          bytes,
          dataUpdatedAt: entry.state.dataUpdatedAt
        }
      ]
    } catch {
      return []
    }
  })

  const totalBytes = entries.reduce((acc, entry) => acc + entry.bytes, 0)
  return {
    entries,
    stats: { totalEntries: rawEntries.length, totalBytes }
  }
}

export const useQueryCacheUsage = () => {
  const [measurement, setMeasurement] = useState<QueryCacheMeasurement>(() =>
    measureQueryCache()
  )
  const [history, setHistory] = useState<QueryCacheSample[]>(() => {
    const initial = measureQueryCache()
    return [{ timestamp: Date.now(), totalBytes: initial.stats.totalBytes }]
  })

  useEffect(() => {
    const liveInterval = setInterval(() => {
      setMeasurement(measureQueryCache())
    }, LIVE_TICK_INTERVAL_MS)

    const sampleInterval = setInterval(() => {
      const next = measureQueryCache()
      setHistory(prev => {
        const sample: QueryCacheSample = {
          timestamp: Date.now(),
          totalBytes: next.stats.totalBytes
        }
        const updated = [...prev, sample]
        return updated.length > MAX_SAMPLES
          ? updated.slice(updated.length - MAX_SAMPLES)
          : updated
      })
    }, GRAPH_SAMPLE_INTERVAL_MS)

    return () => {
      clearInterval(liveInterval)
      clearInterval(sampleInterval)
    }
  }, [])

  return {
    stats: measurement.stats,
    entries: measurement.entries,
    history
  }
}
